import type { KeyboardEvent, MouseEvent } from 'react'
import type { SubTemplateProps } from '@/deskUiSkeletons/rowTemplates/RowTemplateTypes'
import { useNotesDialog } from '../../NotesDialogContext'

function isControl(target: EventTarget | null, card: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false
  const control = target.closest('a,button,input,textarea,select,[role="button"],.md-sub-icon')
  return !!control && control !== card
}

export function NotesSlideTemplate({ display, controls }: SubTemplateProps) {
  const notesDialog = useNotesDialog()
  const media = display.permissions.canViewContent && !display.access.isContentLocked
    ? display.media.primaryAttachment
    : undefined
  const mediaSrc = media?.src || media?.thumbSrc

  function openNote(event: MouseEvent<HTMLElement> | KeyboardEvent<HTMLElement>) {
    if (isControl(event.target, event.currentTarget)) return
    if ('key' in event && event.key !== 'Enter' && event.key !== ' ') return
    event.preventDefault()
    notesDialog.openNote(display.elId)
  }

  return (
    <article
      className={`group relative flex min-h-32 cursor-pointer break-inside-avoid flex-col overflow-hidden rounded-lg border border-[#e0e0e0] text-sm text-[#202124] shadow-none transition-shadow hover:shadow-md focus:outline-none focus:ring-2 focus:ring-[#fbbc04]${display.isChecked ? ' ring-2 ring-[#1a73e8]' : ''}`}
      style={{ backgroundColor: display.backgroundColor }}
      role="button"
      tabIndex={0}
      aria-label={`Preview note ${display.title}`}
      onClick={openNote}
      onKeyDown={openNote}
      data-md-sub-template="notes"
    >
      {mediaSrc ? (
        <img
          className="max-h-64 w-full object-cover"
          src={mediaSrc}
          alt={media.title || media.filename || display.title}
        />
      ) : null}
      <div className="flex items-start gap-2 px-4 pt-3">
        <div className="min-w-0 flex-1 break-words text-base font-medium text-[#202124]">
          <span className="hidden" aria-hidden="true">{controls.content.title}</span>
          {display.title}
        </div>
        <div className="opacity-70 transition-opacity group-hover:opacity-100">{controls.actions.favorite}</div>
      </div>
      <div className="min-h-10 flex-1 whitespace-pre-wrap break-words px-4 py-3 leading-5 text-[#3c4043] [&_.md-sub-text]:whitespace-pre-wrap [&_.md-sub-text]:text-inherit">
        {controls.content.text}
      </div>
      <footer className="flex min-h-11 items-center gap-1 px-2 pb-2 opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100">
        {controls.actions.check}
        {controls.actions.quickReply}
        <span className="ml-auto px-2 text-xs text-[#5f6368]">{controls.content.time}</span>
      </footer>
    </article>
  )
}
